import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../services/authAPI";
import moment from "moment";
import "moment/locale/fr";
import { APIProvider, Map, AdvancedMarker, Pin } from "@vis.gl/react-google-maps";
import Loading from "../components/Loading";

const STATUS_LABELS = {
    ready: "Prêt",
    reoptimizing: "Réoptimisation",
    deleted: "Supprimé",
    uploaded: "Téléchargé",
};

const STATUS_CLASSES = {
    ready: "success",
    reoptimizing: "light",
    deleted: "danger",
    uploaded: "warning",
};

const TYPE_LABELS = {
    locker: "Consigne",
    parcel_shop: "Commerce",
};

const ROUTE_COLORS = [
    "#0d6efd",
    "#198754",
    "#dc3545",
    "#fd7e14",
    "#6f42c1",
    "#20c997",
    "#d63384",
    "#0dcaf0",
    "#6c757d",
    "#ffc107",
];

export default function BatchPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [batch, setBatch] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRouteId, setSelectedRouteId] = useState(null);

    const defaultCenter = { lat: 48.4, lng: -4.2 };

    useEffect(() => {
        async function fetchBatch() {
            try {
                const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/v2/routes/point_visits_batches/${id}`);
                if (!response.ok) {
                    setError("Impossible de charger ce batch.");
                    return;
                }
                const data = await response.json();
                setBatch(data);
            } catch (err) {
                setError("Erreur lors du chargement du batch.");
            } finally {
                setLoading(false);
            }
        }
        fetchBatch();
    }, [id]);

    function formatDeliveryHours(hours) {
        if (!hours || hours.length === 0) return "";
        return hours.map((h) => `${h.opens_at} - ${h.closes_at}`).join(" / ");
    }

    if (loading) return <Loading />;
    if (error) return <div className="alert alert-danger">{error}</div>;
    if (!batch) return null;

    const { point_visits_batch: batchInfo, driver_routes: driverRoutes, unassigned_point_visits: unassigned } = batch;
    const allUnassigned = unassigned || [];
    const hasRoutes = driverRoutes && driverRoutes.length > 0;
    const selectedRoute = hasRoutes ? driverRoutes.find((r) => r.id === selectedRouteId) : null;
    const selectedRouteIndex = hasRoutes ? driverRoutes.findIndex((r) => r.id === selectedRouteId) : -1;

    const sortedPointVisits = selectedRoute
        ? [...selectedRoute.point_visits].sort((a, b) => a.sequence - b.sequence)
        : [];

    return (
        <div className="card mb-4">
            <div className="card-header py-3">
                <div className="row py-3">
                    <div className="col-sm-6 text-center text-sm-start">
                        <h5 className="mb-4 mb-sm-0 mt-2">
                            <span className={"badge badge-" + STATUS_CLASSES[batchInfo.status]}>{STATUS_LABELS[batchInfo.status]}</span> Itinéraire {batchInfo.sorting_center_code} du {moment(batchInfo.scheduled_for).format("dddd DD MMMM YYYY")}
                        </h5>
                    </div>
                    {/* <div className="col-sm-6 text-center text-sm-end">
                        <button
                            type="button"
                            className="btn btn-primary me-2"
                            // onClick={() => { setShowInviteModal(true); setInviteViolations([]); }}
                        >
                            Optimiser
                            <i className="fas fa-envelope ms-2"></i>
                        </button>
                    </div> */}
                </div>
            </div>
            <div className="card-body">
                <div className="operation">
                    <section id="route-section">
                        {selectedRoute ? (
                            <div>
                                <button className="btn btn-outline-primary btn-sm mb-3" onClick={() => setSelectedRouteId(null)}>
                                    Retour aux routes
                                </button>
                                <h5 className="mb-3">{selectedRoute.name} - {selectedRoute.point_visits.length} points</h5>
                                <div className="batch-points-list">
                                    {sortedPointVisits.map((pv) => (
                                        <div className="batch-point-item d-flex gap-2" key={pv.id}>
                                            <div className="flex-shrink-0 pt-1">
                                                <span
                                                    className="point-visit-badge"
                                                    style={{
                                                        backgroundColor: ROUTE_COLORS[selectedRouteIndex % ROUTE_COLORS.length],
                                                        color: "#fff",
                                                    }}
                                                >
                                                    {pv.sequence}
                                                </span>
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <strong>{pv.point.name}</strong>
                                                        <p className="mb-0 text-muted small">{pv.point.address}, {pv.point.city} {pv.point.postal_code}</p>
                                                        <p className="mb-0 small">
                                                            {TYPE_LABELS[pv.point.point_type] || pv.point.point_type} | {pv.point.code}
                                                        </p>
                                                    </div>
                                                    <div className="text-end small text-muted" style={{ whiteSpace: "nowrap" }}>
                                                        {formatDeliveryHours(pv.point.delivery_hours)}
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-3 mt-1 small">
                                                    <span>{pv.point.bags.dropoff_count} dépôts</span>
                                                    <span>{pv.point.bags.pickup_count} collectes</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <>
                                {hasRoutes && (
                                    <>
                                        <h6>Routes ({driverRoutes.length})</h6>
                                        {driverRoutes.map((dr, i) => (
                                            <div
                                                className="card route-card"
                                                key={dr.id}
                                                onClick={() => setSelectedRouteId(dr.id)}
                                            >
                                                <div className="card-body card-route">
                                                    <div className="card-route-info">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span
                                                                style={{
                                                                    width: 12,
                                                                    height: 12,
                                                                    borderRadius: "50%",
                                                                    backgroundColor: ROUTE_COLORS[i % ROUTE_COLORS.length],
                                                                    display: "inline-block",
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                            <strong>{dr.name}</strong>
                                                        </div>
                                                    </div>
                                                    <div className="card-route-progress">
                                                        <small>{dr.point_visits.length} points</small>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}

                                {allUnassigned.length > 0 && (
                                    <>
                                        {hasRoutes && <hr />}
                                        <h6>Points non assignés ({allUnassigned.length})</h6>
                                        <div className="batch-points-list">
                                            {allUnassigned.map((pv) => (
                                                <div className="batch-point-item" key={pv.id}>
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <strong>{pv.point.name}</strong>
                                                            <p className="mb-0 text-muted small">{pv.point.address}, {pv.point.city} {pv.point.postal_code}</p>
                                                            <p className="mb-0 small">
                                                                {TYPE_LABELS[pv.point.point_type] || pv.point.point_type} | {pv.point.code}
                                                            </p>
                                                        </div>
                                                        <div className="text-end small text-muted" style={{ whiteSpace: "nowrap" }}>
                                                            {formatDeliveryHours(pv.point.delivery_hours)}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </section>

                    <section id="map-section">
                        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                            <Map
                                defaultCenter={defaultCenter}
                                defaultZoom={8}
                                mapId="a24a5e7a0ff8f2636489d9a2"
                                style={{ width: "100%", height: "600px" }}
                            >
                                {selectedRoute && (
                                    <>
                                        {selectedRoute.point_visits.map((pv) => (
                                            <AdvancedMarker
                                                key={pv.id}
                                                position={{ lat: Number(pv.point.latitude), lng: Number(pv.point.longitude) }}
                                                title={`${pv.sequence} - ${pv.point.name}`}
                                            >
                                                <div style={{
                                                    background: ROUTE_COLORS[selectedRouteIndex % ROUTE_COLORS.length],
                                                    color: "#fff",
                                                    borderRadius: "50%",
                                                    border: "2px solid #e9ebef",
                                                    width: 24,
                                                    height: 24,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 7,
                                                    fontWeight: "bold",
                                                    }}>
                                                    {pv.sequence}
                                                </div>
                                            </AdvancedMarker>
                                        ))}
                                        {selectedRoute.center && (
                                            <AdvancedMarker
                                                position={{ lat: Number(selectedRoute.center.latitude), lng: Number(selectedRoute.center.longitude) }}
                                                title={`Hub - ${selectedRoute.center.code}`}
                                            >
                                                <Pin background="#000" glyphColor="#fff" borderColor="#000" glyph="H" />
                                            </AdvancedMarker>
                                        )}
                                    </>
                                )}

                                {!selectedRoute && hasRoutes && driverRoutes.map((dr, i) => (
                                    dr.point_visits.map((pv) => (
                                        <AdvancedMarker
                                            key={pv.id}
                                            position={{ lat: Number(pv.point.latitude), lng: Number(pv.point.longitude) }}
                                            title={`${dr.name} - ${pv.point.name}`}
                                        >
                                            <div style={{
                                                background: ROUTE_COLORS[i % ROUTE_COLORS.length],
                                                color: "#fff",
                                                borderRadius: "50%",
                                                border: "2px solid #e9ebef",
                                                width: 24,
                                                height: 24,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 7,
                                                fontWeight: "bold",
                                            }}>
                                                {pv.sequence}
                                            </div>
                                        </AdvancedMarker>
                                    ))
                                ))}

                                {!selectedRoute && allUnassigned.map((pv) => (
                                    <AdvancedMarker
                                        key={pv.id}
                                        position={{ lat: Number(pv.point.latitude), lng: Number(pv.point.longitude) }}
                                        title={`${pv.point.name} (${pv.point.code})`}
                                    >
                                        <div style={{
                                            background: pv.point.point_type === "parcel_shop" ? "#0d6efd" : "#6c757d",
                                            color: "#fff",
                                            borderRadius: "50%",
                                            border: "2px solid #e9ebef",
                                            width: 24,
                                            height: 24,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 8,
                                            fontWeight: "bold",
                                        }}>
                                            {pv.point.point_type === "parcel_shop" ? "PS" : "L"}
                                        </div>
                                    </AdvancedMarker>
                                ))}
                            </Map>
                        </APIProvider>
                    </section>
                </div>
            </div>
        </div>
    );
}
